import React, { useEffect, useState } from "react";
import { Down24, Remove24 } from "@bphxd/ds-core-react/lib/icons";
import SvgSearch24 from "@bphxd/ds-core-react/lib/icons/interface/Search24";
import {
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    InputGroup,
    Input,
    InputGroupText,
} from "reactstrap";
import { Button } from "@bphxd/ds-core-react/lib/components/button/Button";
import { useWindowSize } from "@bphxd/ds-core-react";
import { RecordValidModel } from "../../model/RecordValidModel";

export default function QNASearch({
    onQNASearch,
    setNoData,
    setrawData,
    setSelectedField,
}: {
    onQNASearch: (searchField: string, searchVal: string) => void;
    setNoData: React.Dispatch<React.SetStateAction<string>>;
    setrawData: React.Dispatch<React.SetStateAction<RecordValidModel[]>>;
    setSelectedField: React.Dispatch<React.SetStateAction<string>>;
}) {
    const [searchField, setsearchField] = useState<string>("Select Category");
    const [searchVal, setSearchVal] = useState<string>("");
    const { width } = useWindowSize();

    // Mapping display fields to backend keys
    const getSearchFieldKey = (displayField: string): string => {
        const fieldMapping: { [key: string]: string } = {
            "Order Number": "order_number",
            "Customer ID": "cust_code",
            "Customer Full Name": "cust_acct_name",
            "Vessel Name": "vessel_name",
            "Vessel IMO Number": "vessel_imo_number"
        };
        return fieldMapping[displayField] || displayField.toLowerCase().replace(/ /g, "_");
    };

    const onSearchValChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchVal(e.currentTarget.value);
        setNoData("");
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === "Enter") {
            if (searchField !== "Select Category" && searchVal !== "") {
                event.preventDefault();
                event.stopPropagation();
                validateAndSearch();
            }
        }
    };

    useEffect(() => {
        setNoData("");
        setSearchVal("");
    }, [searchField]);

    // ✅ Field-specific validation
    const validateAndSearch = () => {
        setrawData([]);

        const searchFieldKey = getSearchFieldKey(searchField);

        let isValid = true;
        let errorMsg = "";

        if (searchField === "Customer Full Name") {
            const nameRegex = /^[a-zA-Z\s.'-]+$/;
            if (!nameRegex.test(searchVal)) {
                isValid = false;
                errorMsg = "Only letters, spaces, '.', '-' and ''' are allowed for Customer Name";
            }
        } 
        else if (searchField === "Vessel Name") {
            const vesselNameRegex = /^[a-zA-Z\s.'-]+$/;
            if (!vesselNameRegex.test(searchVal)) {
                isValid = false;
                errorMsg = "Only letters, spaces, '.', '-' and ''' are allowed for Vessel Name";
            }
        }
        else if (searchField === "Vessel IMO Number") {
            const imoRegex = /^[0-9]+$/;
            if (!imoRegex.test(searchVal)) {
                isValid = false;
                errorMsg = "Vessel IMO Number should contain only digits";
            }
        } 
        else {
            const defaultRegex = /^[a-zA-Z0-9]+$/;
            if (!defaultRegex.test(searchVal)) {
                isValid = false;
                errorMsg = "Only letters and numbers are allowed (no special characters)";
            }
        }

        if (!isValid) {
            setNoData(errorMsg);
            return;
        }

        setSelectedField(searchFieldKey);
        const decodedSearchVal = decodeURIComponent(searchVal);
        onQNASearch(searchFieldKey, decodedSearchVal);
        onQNASearch(searchFieldKey, searchVal);
    };

    return (
        <>
            <div className="row"></div>

            <div className="row">
                {/* Dropdown */}
                <div className="col-sm-3 col-lg-2 upd-srch-drop">
                    <UncontrolledDropdown size="sm" className="mb-4">
                        <DropdownToggle
                            className="w-100 sch-drpdwn-toggle-uld rounded-0 border-0 px-0 ft-size"
                            color="#fff"
                            caret
                        >
                            {searchField}
                            <Down24
                                color="#007f00 !important"
                                className="btn-icon-suffix down-arrow-color"
                            />
                        </DropdownToggle>

                        <DropdownMenu className="dropdown-menu-sm w-100 ft-size">
                            <DropdownItem onClick={() => setsearchField("Order Number")}>
                                Order Number
                            </DropdownItem>
                            <DropdownItem onClick={() => setsearchField("Customer ID")}>
                                Customer ID
                            </DropdownItem>
                            <DropdownItem onClick={() => setsearchField("Customer Full Name")}>
                                Customer Full Name
                            </DropdownItem>
                            <DropdownItem onClick={() => setsearchField("Vessel Name")}>
                                Vessel Name
                            </DropdownItem>
                            <DropdownItem onClick={() => setsearchField("Vessel IMO Number")}>
                                Vessel IMO Number
                            </DropdownItem>
                        </DropdownMenu>
                    </UncontrolledDropdown>
                </div>

                {/* Input */}
                <div className="col-sm-7 col-lg-5">
                    <InputGroup className="input-group-merge mb-4 search-input" size="sm">
                        <Input
                            className="form-control-prepended form-control-appended ft-size"
                            placeholder={
                                searchField === "Select Category"
                                    ? "Search"
                                    : searchField === "Order Number" && width > 576
                                        ? "Search With New Marine Order Number"
                                        : searchField === "Customer Full Name"
                                            ? "Search With Customer Full Name"
                                            : searchField === "Vessel IMO Number"
                                                ? "Search With Vessel IMO Number"
                                                : searchField === "Vessel Name"?" Search With Vessel Name":`Search with ${searchField} (Sold-To-Id)`
                            }
                            type="text"
                            onChange={onSearchValChange}
                            onKeyDown={onKeyDown}
                            value={searchVal}
                        />

                        <div className="input-group-prepend">
                            <InputGroupText>
                                <SvgSearch24 color="#007f00" />
                            </InputGroupText>
                        </div>

                        <div className="input-group-append">
                            <InputGroupText
                                onClick={() => {
                                    setSearchVal("");
                                    setNoData("");
                                }}
                            >
                                {searchVal !== "" && (
                                    <Remove24 style={{ cursor: "pointer" }} />
                                )}
                            </InputGroupText>
                        </div>
                    </InputGroup>
                </div>

                {/* Button */}
                <div className="col-sm-2">
                    <Button
                        rounded="pill"
                        theme="brand"
                        level="primary"
                        size="sm"
                        style={{ width: "78px" }}
                        disabled={
                            !(searchField !== "Select Category" && searchVal !== "")
                        }
                        onClick={validateAndSearch}
                        className="upd-sch-btn mb-4 ft-size"
                    >
                        Search
                    </Button>
                </div>
            </div>
        </>
    );
}